<script setup>
// Meta Ads > Genel Bakis: secili reklam hesabinin tek ekranda ozeti.
//
// Sayfa yedi ayri MCP aracindan beslenir. Araclar birbirinden bagimsizdir ve
// biri izin yuzunden patlayabilir (or. fatura bilgisi yalnizca hesap sahibine
// acik). Bu yuzden her bolum kendi hata ve yukleme durumunu tutar; bir aracin
// hatasi ekranin geri kalanini bos birakmaz.
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { aracCalistir, diziyeCevir, paraBicimle, sayiBicimle } from './kopru.js';
import AracTablosu from './AracTablosu.vue';
import MetaAdsKabuk from './MetaAdsKabuk.vue';

const { t } = useI18n();
const route = useRoute();

const seciliHesap = ref('');
const hesapDetay = ref(null);

const bolumOlustur = () => ({ sonuc: null, hata: '', yukleniyor: false });

const bolumler = reactive({
  portfoy: bolumOlustur(),
  hesapBilgi: bolumOlustur(),
  firsat: bolumOlustur(),
  hatalar: bolumOlustur(),
  kota: bolumOlustur(),
  fatura: bolumOlustur(),
  limit: bolumOlustur(),
});

// Vue, slot prop adlarini camelCase'e cevirmez; kabuk ":hesap-ayrinti" ile
// baglaniyor. Iki yazimi da okuyoruz ki kabuk ileride degisse de kirilmasin.
const kabukAyrinti = kabuk =>
  kabuk?.hesapAyrinti || kabuk?.['hesap-ayrinti'] || null;

// Kabuk secili hesabi yalnizca slot prop'u olarak verir, <script setup>
// icinden gorunmez. Bu gorunmeyen ara bilesen degerleri yerel ref'lere tasir;
// boylece hesap degisimi siradan bir watch ile izlenebiliyor.
const HesapKoprusu = {
  props: {
    hesap: { type: String, default: '' },
    ayrinti: { type: Object, default: null },
  },
  setup(kendiProps) {
    watch(
      () => kendiProps.hesap,
      yeni => {
        seciliHesap.value = yeni || '';
      },
      { immediate: true }
    );
    watch(
      () => kendiProps.ayrinti,
      yeni => {
        hesapDetay.value = yeni || null;
      },
      { immediate: true }
    );
    return () => null;
  },
};

// Hesap secici hizlica degistirilirse eski istegin yaniti yeninin ardindan
// donebilir. Her yukleme turuna sira numarasi veriyoruz; yalnizca en son tur
// ekrana yazabilir. Aksi halde A hesabinin harcamasi B hesabinin ekraninda
// dogru rakam gibi durur.
let sonIstek = 0;

const bolumYukle = async (anahtar, arac, argumanlar, istekNo) => {
  const bolum = bolumler[anahtar];
  bolum.yukleniyor = true;
  bolum.hata = '';
  try {
    const sonuc = await aracCalistir(route.params.accountId, arac, argumanlar);
    if (istekNo !== sonIstek) return;
    bolum.sonuc = sonuc;
  } catch (e) {
    if (istekNo !== sonIstek) return;
    // Eski veriyi biraktirmiyoruz: baska bir hesabin rakamini dogru sanmak,
    // hic rakam gormemekten daha tehlikeli.
    bolum.sonuc = null;
    bolum.hata = e.response?.data?.error || e.message;
  } finally {
    // Gecmis turun geri donusu yeni turun spinner'ini kapatmasin.
    if (istekNo === sonIstek) bolum.yukleniyor = false;
  }
};

const yukle = async () => {
  if (!seciliHesap.value) return;
  sonIstek += 1;
  const istekNo = sonIstek;
  const hesapArg = { account_id: seciliHesap.value };
  // allSettled: bolumYukle hatayi kendi icinde yutsa da, ileride eklenecek bir
  // cagri patlarsa diger cagrilarin yarida kesilmemesini garanti eder.
  await Promise.allSettled([
    bolumYukle('portfoy', 'ads_portfolio_summary', {}, istekNo),
    bolumYukle('hesapBilgi', 'ads_get_account_info', hesapArg, istekNo),
    bolumYukle('firsat', 'ads_get_opportunity_score', hesapArg, istekNo),
    bolumYukle('hatalar', 'ads_get_errors', hesapArg, istekNo),
    bolumYukle('kota', 'ads_rate_status', {}, istekNo),
    bolumYukle('fatura', 'ads_get_billing_info', hesapArg, istekNo),
    bolumYukle('limit', 'ads_get_spend_limit', hesapArg, istekNo),
  ]);
};

watch(seciliHesap, yukle, { immediate: true });

const herhangiYukleniyor = computed(() =>
  Object.values(bolumler).some(b => b.yukleniyor)
);

const ozetAl = bolum => bolum.sonuc?.ozet || '';

/**
 * Araclar veriyi bazen dogrudan, bazen tek anahtarli bir zarf icinde
 * ({ account: {...} } gibi) dondurur. Beklenen alanlar ust seviyede yoksa bir
 * kademe iceri bakariz.
 */
const nesneCoz = (veri, ornekAlanlar) => {
  if (!veri || typeof veri !== 'object' || Array.isArray(veri)) return {};
  if (ornekAlanlar.some(a => veri[a] !== undefined)) return veri;
  const ic = Object.values(veri).find(
    d => d && typeof d === 'object' && !Array.isArray(d)
  );
  return ic || veri;
};

const hesapBilgi = computed(() =>
  nesneCoz(bolumler.hesapBilgi.sonuc?.veri, [
    'amount_spent',
    'account_status',
    'balance',
    'currency',
  ])
);

// Kabuktan gelen hesap listesi yalnizca ozet alanlari tasir; eksik kalani
// ads_get_account_info yanitindan tamamlariz.
const hesapAlani = ad => {
  const d = hesapDetay.value?.[ad];
  if (d !== undefined && d !== null && d !== '') return d;
  return hesapBilgi.value?.[ad];
};

/** Yanittaki ilk sayisal alani bulur; alan adi arac surumune gore degisiyor. */
const sayisalBul = (veri, adaylar) => {
  const nesne = nesneCoz(veri, adaylar);
  const bulunan = adaylar.find(a => {
    const d = nesne[a];
    return d !== undefined && d !== null && d !== '' && !Number.isNaN(Number(d));
  });
  return bulunan ? Number(nesne[bulunan]) : null;
};

const harcamaLimiti = computed(() =>
  sayisalBul(bolumler.limit.sonuc?.veri, [
    'spend_cap',
    'spend_limit',
    'daily_spend_limit',
    'limit',
    'amount',
  ])
);

const bosMu = d => d === undefined || d === null || d === '';

// paraBicimle bos degeri 0 sayar; alan hic gelmediginde "0,00 ₺" yazmak
// kullaniciya "harcama yok" dedirtir. Veri yoksa tire gosteriyoruz.
const paraVeyaTire = (deger, birim) =>
  bosMu(deger) ? '—' : paraBicimle(deger, birim);

// Bos dizi/nesne de aslinda "veri yok" demek; panelde bos satir kaplamasin.
const doluMu = deger => {
  if (bosMu(deger)) return false;
  if (Array.isArray(deger)) return deger.length > 0;
  if (typeof deger === 'object') return Object.keys(deger).length > 0;
  return true;
};

/**
 * Meta tarihleri bazen ISO metin ("2024-05-01T10:23:45+0000"), bazen unix
 * damgasi dondurur. Ham hali kullaniciya hicbir sey anlatmadigi icin yerel
 * bicime ceviriyoruz; cozulemeyen degeri ham birakiyoruz, cunku uydurulmus
 * bir tarih hic tarih gostermemekten daha yaniltici olur.
 */
const tarihBicimle = deger => {
  if (bosMu(deger)) return '';
  const metin = String(deger).trim();
  // Yalniz rakamdan olusan deger damgadir; 10 haneli saniyeyi milisaniyeye
  // cikarmazsak 1970 tarihleri cikar.
  const tarih = /^\d{9,13}$/.test(metin)
    ? new Date(metin.length <= 10 ? Number(metin) * 1000 : Number(metin))
    : new Date(metin);
  if (Number.isNaN(tarih.getTime())) return metin;
  // Saat tasimayan alanlarda (fatura gunu gibi) "00:00" yazmak gurultu olur.
  const yalnizGun = /^\d{4}-\d{2}-\d{2}$/.test(metin);
  return new Intl.DateTimeFormat(
    'tr-TR',
    yalnizGun ? { dateStyle: 'medium' } : { dateStyle: 'medium', timeStyle: 'short' }
  ).format(tarih);
};

// Meta alan adlari teknik; ekranda is dilinde karsiliklarini gosteriyoruz.
// Haritada olmayan anahtar oldugu gibi kalir - yanlis etiket uydurmaktansa
// ham alan adi dogru bilgidir.
const ALAN_ETIKETLERI = {
  account_id: 'HESAP_NO',
  account_name: 'HESAP_ADI',
  name: 'AD',
  account_status: 'HESAP_DURUMU',
  currency: 'PARA_BIRIMI',
  balance: 'BAKIYE',
  amount_spent: 'HARCANAN',
  spend_cap: 'HARCAMA_LIMITI',
  business_name: 'ISLETME',
  funding_source: 'ODEME_KAYNAGI',
  payment_method: 'ODEME_YONTEMI',
  next_bill_date: 'SONRAKI_FATURA',
  billing_period: 'FATURA_DONEMI',
  timezone_name: 'SAAT_DILIMI',
  created_time: 'OLUSTURULMA',
  updated_time: 'GUNCELLENME',
  status: 'DURUM',
  total: 'TOPLAM',
  count: 'ADET',
  usage: 'KULLANIM',
  limit: 'LIMIT',
  reset_time: 'SIFIRLANMA',
  disable_reason: 'KAPATMA_NEDENI',
};

// Meta para alanlarini kurus cinsinden verir; paraBicimle bolmeyi yapar.
// "limit" bilerek disarida: kota bolumunde cagri adedi demek, para degil.
const PARA_ALANLARI = [
  'amount_spent',
  'balance',
  'spend_cap',
  'spend_limit',
  'daily_spend_limit',
  'budget',
  'daily_budget',
  'lifetime_budget',
  'budget_remaining',
  'min_daily_budget',
];

const TARIH_ALANLARI = [
  'created_time',
  'updated_time',
  'next_bill_date',
  'reset_time',
  'start_time',
  'stop_time',
  'time',
  'timestamp',
];

const okunurDeger = (anahtar, deger, birim) => {
  // Ic ice yapiyi dar panele basmak okunmaz olur: duz ogeleri virgulle
  // listeliyoruz, nesne tasiyan dizide yalnizca adedini soyluyoruz.
  if (Array.isArray(deger)) {
    const duz = deger.every(d => d === null || typeof d !== 'object');
    if (duz) return deger.join(', ');
    return t('META_ADS.GENEL_BAKIS.OGE_SAYISI', {
      sayi: sayiBicimle(deger.length),
    });
  }
  if (deger && typeof deger === 'object') {
    return t('META_ADS.GENEL_BAKIS.OGE_SAYISI', {
      sayi: sayiBicimle(Object.keys(deger).length),
    });
  }
  if (typeof deger === 'boolean') {
    return deger ? t('META_ADS.ORTAK.EVET') : t('META_ADS.ORTAK.HAYIR');
  }
  if (PARA_ALANLARI.includes(anahtar)) return paraBicimle(deger, birim);
  if (TARIH_ALANLARI.includes(anahtar)) return tarihBicimle(deger);
  // Kimlik alanlarina binlik ayraci girerse numara okunmaz hale gelir.
  if (anahtar === 'id' || anahtar.endsWith('_id')) return String(deger);
  if (typeof deger === 'number') return sayiBicimle(deger);
  return String(deger);
};

/**
 * Arac yaniti bazen tek anahtarli bir zarf ({ account: {...} }), bazen tek
 * kayitlik bir dizi olur. Panelde gosterilecek asil nesneyi cikariyoruz.
 */
const panelNesnesi = veri => {
  let d = veri;
  for (let i = 0; i < 3; i += 1) {
    if (Array.isArray(d) && d.length === 1) {
      d = d[0];
    } else if (d && typeof d === 'object' && !Array.isArray(d)) {
      const anahtarlar = Object.keys(d);
      const tek = anahtarlar.length === 1 ? d[anahtarlar[0]] : null;
      if (!tek || typeof tek !== 'object') break;
      d = tek;
    } else {
      break;
    }
  }
  return d && typeof d === 'object' && !Array.isArray(d) ? d : null;
};

/**
 * Nesneyi anahtar/deger satirlarina cevirir. Arac insan okunur ozet
 * donduremediginde panel bu satirlari basar; aksi halde elimizdeki veri
 * ya ham JSON gibi ya da hic gorunmeden kaybolurdu.
 */
const okunurAlanlar = veri => {
  const nesne = panelNesnesi(veri);
  if (!nesne) return [];
  const birim = hesapAlani('currency') || 'TRY';
  return Object.keys(nesne)
    .filter(a => doluMu(nesne[a]))
    // Panel dar; ilk on iki alan ozet icin yeter, gerisi tabloya ait.
    .slice(0, 12)
    .map(a => ({
      anahtar: a,
      etiket: ALAN_ETIKETLERI[a]
        ? t(`META_ADS.GENEL_BAKIS.ALAN.${ALAN_ETIKETLERI[a]}`)
        : a,
      deger: okunurDeger(a, nesne[a], birim),
    }))
    .filter(s => s.deger !== '');
};

const kartlar = computed(() => {
  const birim = hesapAlani('currency') || 'TRY';
  const durumKodu = hesapAlani('account_status');
  // Meta'da 1 = aktif. Kod hic gelmediyse "kapali" demek yanlis alarm olur;
  // bilinmiyor durumu ayri tutuluyor.
  const durumBilinmiyor = bosMu(durumKodu);
  const aktif = Number(durumKodu) === 1;
  const limit = harcamaLimiti.value;
  return [
    {
      anahtar: 'harcama',
      baslik: t('META_ADS.GENEL_BAKIS.TOPLAM_HARCAMA'),
      deger: paraVeyaTire(hesapAlani('amount_spent'), birim),
    },
    {
      anahtar: 'bakiye',
      baslik: t('META_ADS.GENEL_BAKIS.BAKIYE'),
      deger: paraVeyaTire(hesapAlani('balance'), birim),
    },
    {
      anahtar: 'durum',
      baslik: t('META_ADS.GENEL_BAKIS.HESAP_DURUMU'),
      deger: durumBilinmiyor
        ? '—'
        : aktif
          ? t('META_ADS.GENEL_BAKIS.AKTIF')
          : t('META_ADS.GENEL_BAKIS.KAPALI'),
      renk: durumBilinmiyor
        ? 'text-n-slate-11'
        : aktif
          ? 'text-n-teal-11'
          : 'text-n-ruby-11',
    },
    {
      anahtar: 'limit',
      baslik: t('META_ADS.GENEL_BAKIS.HARCAMA_LIMITI'),
      deger: limit
        ? paraBicimle(limit, birim)
        : t('META_ADS.GENEL_BAKIS.LIMIT_YOK'),
      hata: bolumler.limit.hata,
    },
  ];
});

const firsatVerisi = computed(() =>
  nesneCoz(bolumler.firsat.sonuc?.veri, ['score', 'opportunity_score'])
);

const firsatPuani = computed(() => {
  const v = firsatVerisi.value;
  const ham = v.score ?? v.opportunity_score ?? v.value ?? null;
  return ham === null || ham === '' || Number.isNaN(Number(ham))
    ? null
    : Number(ham);
});

const firsatOnerileri = computed(() => {
  const v = firsatVerisi.value;
  const dizi = [v.recommendations, v.suggestions, v.opportunities, v.items].find(
    Array.isArray
  );
  return (dizi || [])
    .map(o =>
      typeof o === 'string'
        ? o
        : o?.message || o?.title || o?.description || o?.name || ''
    )
    .filter(Boolean);
});

const hataSatirlari = computed(() =>
  bolumler.hatalar.sonuc ? diziyeCevir(bolumler.hatalar.sonuc) : []
);

// Hata kaydinin alan adlari araca gore degisir. Bilinen adlardan hicbiri yoksa
// sutunlari bos birakiriz: AracTablosu o zaman sutunlari kendisi turetir ve
// veri ekranda gorunmeden kaybolmaz.
const hataSutunlari = computed(() => {
  const ilk = hataSatirlari.value[0];
  const bilinen = [
    'message',
    'error_message',
    'title',
    'summary',
    'code',
    'error_code',
  ];
  if (!ilk || typeof ilk !== 'object') return [];
  if (!bilinen.some(a => a in ilk)) return [];
  return [
    {
      anahtar: 'time',
      baslik: t('META_ADS.GENEL_BAKIS.HATA_ZAMAN'),
      // Ham damga ("1714553025") ya da ISO metni kullaniciya bir sey
      // anlatmiyor; yerel tarih/saat bicimine ceviriyoruz.
      bicim: (deger, satir) =>
        tarihBicimle(satir.time || satir.created_time || satir.timestamp) ||
        '—',
    },
    {
      anahtar: 'message',
      baslik: t('META_ADS.GENEL_BAKIS.HATA_MESAJ'),
      bicim: (deger, satir) =>
        satir.message ||
        satir.error_message ||
        satir.title ||
        satir.summary ||
        '—',
    },
    {
      anahtar: 'code',
      baslik: t('META_ADS.GENEL_BAKIS.HATA_KOD'),
      bicim: (deger, satir) =>
        String(satir.code ?? satir.error_code ?? satir.error_subcode ?? '—'),
    },
  ];
});

// Bu uc bolum ozet metni donmediginde elimizde yalnizca nesne kaliyordu;
// "alanlar" o nesnenin okunur anahtar/deger karsiligi.
const bilgiPanelleri = computed(() => [
  {
    anahtar: 'portfoy',
    baslik: t('META_ADS.GENEL_BAKIS.PORTFOY'),
    metin: ozetAl(bolumler.portfoy),
    alanlar: okunurAlanlar(bolumler.portfoy.sonuc?.veri),
    hata: bolumler.portfoy.hata,
    yukleniyor: bolumler.portfoy.yukleniyor,
  },
  {
    anahtar: 'fatura',
    baslik: t('META_ADS.GENEL_BAKIS.FATURA'),
    metin: ozetAl(bolumler.fatura),
    alanlar: okunurAlanlar(bolumler.fatura.sonuc?.veri),
    hata: bolumler.fatura.hata,
    yukleniyor: bolumler.fatura.yukleniyor,
  },
  {
    anahtar: 'kota',
    baslik: t('META_ADS.GENEL_BAKIS.KOTA'),
    metin: ozetAl(bolumler.kota),
    alanlar: okunurAlanlar(bolumler.kota.sonuc?.veri),
    hata: bolumler.kota.hata,
    yukleniyor: bolumler.kota.yukleniyor,
  },
]);
</script>

<template>
  <MetaAdsKabuk :baslik="t('META_ADS.GENEL_BAKIS.BASLIK')" :sekmeler="[]">
    <template #eylemler>
      <Button
        variant="faded"
        size="sm"
        icon="i-lucide-refresh-cw"
        :label="t('META_ADS.ORTAK.YENILE')"
        :disabled="!seciliHesap || herhangiYukleniyor"
        @click="yukle"
      />
    </template>

    <template #default="kabuk">
      <HesapKoprusu :hesap="kabuk.hesap" :ayrinti="kabukAyrinti(kabuk)" />

      <!-- Hesap listesi henuz gelmediyse "hesap secin" demek yaniltici; o an
           secilecek bir sey yok. Once bekleme gosteriliyor. -->
      <div
        v-if="kabuk.yukleniyor && !kabuk.hesap"
        class="flex justify-center p-10"
      >
        <Spinner class="text-n-brand" />
      </div>

      <p v-else-if="!kabuk.hesap" class="p-10 text-sm text-center text-n-slate-11">
        {{ t('META_ADS.GENEL_BAKIS.HESAP_SECIN') }}
      </p>

      <div v-else class="flex flex-col gap-6 p-6">
        <p v-if="bolumler.hesapBilgi.hata" class="m-0 text-sm text-n-ruby-11">
          {{ bolumler.hesapBilgi.hata }}
        </p>

        <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div
            v-for="kart in kartlar"
            :key="kart.anahtar"
            class="p-4 border rounded-xl border-n-weak"
          >
            <p class="m-0 text-xs text-n-slate-11">{{ kart.baslik }}</p>
            <p
              class="m-0 mt-1 text-lg font-medium"
              :class="kart.renk || 'text-n-slate-12'"
            >
              {{ kart.deger }}
            </p>
            <p v-if="kart.hata" class="m-0 mt-1 text-xs text-n-ruby-11">
              {{ kart.hata }}
            </p>
          </div>
        </div>

        <section class="p-4 border rounded-xl border-n-weak">
          <div class="flex items-center gap-3">
            <h2 class="m-0 text-sm font-medium text-n-slate-12">
              {{ t('META_ADS.GENEL_BAKIS.FIRSAT_PUANI') }}
            </h2>
            <Spinner v-if="bolumler.firsat.yukleniyor" class="text-n-brand" />
            <span class="grow" />
            <span
              v-if="firsatPuani !== null"
              class="text-2xl font-medium text-n-slate-12"
            >
              {{ sayiBicimle(firsatPuani) }}
            </span>
          </div>

          <p v-if="bolumler.firsat.hata" class="m-0 mt-2 text-sm text-n-ruby-11">
            {{ bolumler.firsat.hata }}
          </p>
          <p
            v-else-if="ozetAl(bolumler.firsat)"
            class="m-0 mt-2 text-sm whitespace-pre-wrap text-n-slate-11"
          >
            {{ ozetAl(bolumler.firsat) }}
          </p>
          <p
            v-else-if="firsatPuani === null && !bolumler.firsat.yukleniyor"
            class="m-0 mt-2 text-sm text-n-slate-11"
          >
            {{ t('META_ADS.GENEL_BAKIS.PUAN_YOK') }}
          </p>

          <template v-if="firsatOnerileri.length">
            <p class="m-0 mt-3 text-xs text-n-slate-11">
              {{ t('META_ADS.GENEL_BAKIS.ONERILER') }}
            </p>
            <ul class="pl-4 m-0 mt-1 list-disc">
              <li
                v-for="(oneri, i) in firsatOnerileri"
                :key="i"
                class="text-sm text-n-slate-12"
              >
                {{ oneri }}
              </li>
            </ul>
          </template>
        </section>

        <section class="border rounded-xl border-n-weak">
          <div class="flex items-center gap-2 px-4 py-3 border-b border-n-weak">
            <h2 class="m-0 text-sm font-medium text-n-slate-12">
              {{ t('META_ADS.GENEL_BAKIS.SON_HATALAR') }}
            </h2>
            <span v-if="hataSatirlari.length" class="text-xs text-n-slate-11">
              {{ sayiBicimle(hataSatirlari.length) }}
            </span>
          </div>

          <p
            v-if="bolumler.hatalar.hata"
            class="px-4 py-3 m-0 text-sm text-n-ruby-11"
          >
            {{ bolumler.hatalar.hata }}
          </p>
          <AracTablosu
            v-else
            :satirlar="hataSatirlari"
            :sutunlar="hataSutunlari"
            :yukleniyor="bolumler.hatalar.yukleniyor"
            :bos-metin="t('META_ADS.GENEL_BAKIS.HATA_YOK')"
          />
        </section>

        <div class="grid gap-3 lg:grid-cols-3">
          <section
            v-for="panel in bilgiPanelleri"
            :key="panel.anahtar"
            class="p-4 border rounded-xl border-n-weak"
          >
            <p class="m-0 text-xs text-n-slate-11">{{ panel.baslik }}</p>
            <Spinner v-if="panel.yukleniyor" class="mt-2 text-n-brand" />
            <p v-else-if="panel.hata" class="m-0 mt-1 text-sm text-n-ruby-11">
              {{ panel.hata }}
            </p>
            <p
              v-else-if="panel.metin"
              class="m-0 mt-1 text-sm whitespace-pre-wrap text-n-slate-12"
            >
              {{ panel.metin }}
            </p>
            <!-- Ozet metni yoksa nesneyi ham basmak yerine anahtar/deger
                 satirlarina dokuyoruz. -->
            <dl
              v-else-if="panel.alanlar.length"
              class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 m-0 mt-2 text-sm"
            >
              <template v-for="alan in panel.alanlar" :key="alan.anahtar">
                <dt class="m-0 text-n-slate-11">{{ alan.etiket }}</dt>
                <dd class="m-0 break-words text-n-slate-12">
                  {{ alan.deger }}
                </dd>
              </template>
            </dl>
            <p v-else class="m-0 mt-1 text-sm text-n-slate-11">
              {{ t('META_ADS.ORTAK.VERI_YOK') }}
            </p>
          </section>
        </div>
      </div>
    </template>
  </MetaAdsKabuk>
</template>
